import { useEffect, useState } from 'react';

/**
 * Hook that triggers after the browser is idle or after a fallback timeout.
 * Uses requestIdleCallback where available, with a setTimeout fallback.
 * 
 * Unlike useOnInteraction, this fires automatically without requiring
 * user interaction — ensuring styles/components load promptly while still
 * deferring non-critical work past the initial paint.
 * 
 * @param timeoutMs - Maximum time to wait before triggering (default: 1500ms)
 */
export function useIdleLoad(timeoutMs = 1500): boolean {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready) return;

    let cancelled = false;

    const trigger = () => {
      if (!cancelled) setReady(true);
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(trigger, { timeout: timeoutMs });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    // Fallback for Safari and others without requestIdleCallback
    const timerId = setTimeout(trigger, Math.min(timeoutMs, 200));
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [ready, timeoutMs]);

  return ready;
}
